import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
const msg = (e, fallback) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : fallback; };
import { Factory, GraduationCap, Plus, Save, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const API = `${window.location.origin}/api/budget`;

/** One manufacturer record per name in procurement's master (`db.manufacturers`); Basis of Design reads it. */
export default function AdminManufacturers() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});       // manu_id → { name, keywords, scopes }
  const [adding, setAdding] = useState({});   // scope_code → name
  const [busy, setBusy] = useState("");

  const load = async () => {
    try { setData((await axios.get(`${API}/admin/manufacturers`)).data); }
    catch (e) { toast.error(msg(e, "Could not load manufacturers")); }
  };
  useEffect(() => { load(); }, []);

  const save = async (m) => {
    const patch = edit[m.id] || {};
    setBusy(m.id);
    try {
      await axios.put(`${API}/admin/manufacturers/${m.id}`, {
        name: patch.name ?? m.name,
        acronym: patch.acronym ?? m.acronym ?? "",
        website: patch.website ?? m.website ?? "",
        keywords: String(patch.keywords ?? (m.keywords || []).join(", ")).split(","),
      });
      setEdit((e) => ({ ...e, [m.id]: undefined }));
      await load();
      toast.success("Saved to the master manufacturer list");
    } catch (e) { toast.error(msg(e, "Could not save")); } finally { setBusy(""); }
  };

  const add = async (code) => {
    const name = (adding[code] || "").trim();
    if (!name) return;
    try {
      await axios.post(`${API}/admin/manufacturers`, { name, scopes: [code], keywords: [] });
      setAdding((a) => ({ ...a, [code]: "" }));
      await load();
      toast.success("Saved to the master manufacturer list");
    } catch (e) { toast.error(msg(e, "Could not add that manufacturer")); }
  };

  const toggleTracked = async (s, on) => {
    try {
      await axios.put(`${API}/admin/scopes/${s.id}/spec-tracked`, { spec_tracked: on });
      setData((d) => ({ ...d, scopes: d.scopes.map((x) => (x.id === s.id ? { ...x, spec_tracked: on } : x)) }));
    } catch (e) { toast.error(msg(e, "Could not change that")); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Permanently delete ${m.name}? This cannot be undone. Existing budgets and Basis of Design rows keep the name as text.`)) return;
    try {
      await axios.delete(`${API}/admin/manufacturers/${m.id}`);
      await load();
      toast.success(`${m.name} deleted`);
    } catch (e) { toast.error(msg(e, "Could not delete that")); }
  };

  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 space-y-6" data-testid="bb-admin-manufacturers">
      <div data-testid="manufacturers-header">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Manufacturers</h1>
        <p className="text-sm text-slate-500 mt-0.5">The approved manufacturers for each scope, with the acronym used on budgets. Turn a scope on for Basis of Design to track it on the BOD board.</p>
      </div>

      {data.scopes.map((s) => {
        const manus = s.manufacturers || [];
        return (
          <div key={s.id} className="bg-card text-card-foreground border border-border rounded-sm" data-testid={`manu-scope-${s.code}`}>
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
              <Factory className="w-4 h-4 text-slate-500" />
              <span className="font-mono text-xs text-slate-500">{s.code}</span>
              <span className="text-sm font-semibold">{s.name}</span>
              {!s.is_active && <span className="text-xs text-amber-700">inactive</span>}
              <div className="ml-auto flex items-center gap-2">
                {s.spec_tracked && <Badge className="text-xs rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">basis of design</Badge>}
                <span className="text-xs text-muted-foreground">Basis of Design</span>
                <Switch checked={!!s.spec_tracked} onCheckedChange={(v) => toggleTracked(s, v)} data-testid={`manu-tracked-${s.code}`} />
              </div>
            </div>
            <div className="divide-y divide-border">
              {manus.map((m) => {
                const p = edit[m.id] || {};
                const dirty = p.name !== undefined || p.keywords !== undefined || p.acronym !== undefined || p.website !== undefined;
                return (
                  <div key={m.id} className="px-4 py-2 flex flex-wrap items-center gap-2" data-testid={`manu-row-${m.id}`}>
                    <Input value={p.name ?? m.name} onChange={(e) => setEdit((x) => ({ ...x, [m.id]: { ...p, name: e.target.value } }))}
                           className="h-8 w-48 text-sm" data-testid={`manu-name-${m.id}`} />
                    <Input value={p.keywords ?? (m.keywords || []).join(", ")}
                           onChange={(e) => setEdit((x) => ({ ...x, [m.id]: { ...p, keywords: e.target.value } }))}
                           placeholder="name variants, comma separated" className="h-8 flex-1 min-w-[180px] text-sm"
                           data-testid={`manu-keywords-${m.id}`} />
                    <Input value={p.acronym ?? m.acronym ?? ""} maxLength={8} placeholder="Acronym" title="Acronym — shown in the Basis of Design MANUF column"
                           onChange={(e) => setEdit((x) => ({ ...x, [m.id]: { ...p, acronym: e.target.value.toUpperCase() } }))}
                           className="h-8 w-24 text-sm font-mono uppercase" data-testid={`manu-acronym-${m.id}`} />
                    <Input value={p.website ?? m.website ?? ""} placeholder="Website" title="Website — the MANUF acronym links here on the Basis of Design"
                           onChange={(e) => setEdit((x) => ({ ...x, [m.id]: { ...p, website: e.target.value } }))}
                           className="h-8 w-44 text-sm" data-testid={`manu-website-${m.id}`} />
                    <div className="flex flex-wrap gap-1">
                      {(m.keywords || []).map((k) => (
                        <Badge key={k} className="text-xs rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1">
                          <GraduationCap className="w-3 h-3" />{k}
                        </Badge>
                      ))}
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Save" disabled={!dirty || busy === m.id} onClick={() => save(m)} data-testid={`manu-save-${m.id}`}>
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600"
                            title="Permanently delete manufacturer" onClick={() => remove(m)} data-testid={`manu-delete-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              <div className="px-4 py-2 flex items-center gap-2">
                <Input value={adding[s.code] || ""} onChange={(e) => setAdding((a) => ({ ...a, [s.code]: e.target.value }))}
                       onKeyDown={(e) => e.key === "Enter" && add(s.code)} placeholder="Add a manufacturer for this scope…"
                       className="h-8 w-72 text-sm" data-testid={`manu-add-${s.code}`} />
                <Button size="sm" variant="outline" onClick={() => add(s.code)} data-testid={`manu-add-btn-${s.code}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" />Add
                </Button>
              </div>
            </div>
          </div>
        );
      })}

    </div>
  );
}
